import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import * as Sentry from '@sentry/react';

const ErrorWrapper = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 40px 16px;
  text-align: center;
`;

const ErrorIcon = styled.div`
  width: 80px;
  height: 80px;
  background-color: #fee2e2;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;

  svg {
    width: 40px;
    height: 40px;
    color: #ef4444;
  }
`;

const ErrorTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px 0;
`;

const ErrorMessage = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0 0 24px 0;
`;

const RetryButton = styled.button`
  padding: 12px 24px;
  background-color: #3b82f6;
  color: white;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-right: 12px;

  &:hover {
    background-color: #2563eb;
  }
`;

const HomeButton = styled.button`
  padding: 12px 24px;
  background-color: white;
  color: #333;
  font-size: 16px;
  font-weight: 600;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background-color: #f9fafb;
  }
`;

/**
 * Error boundary component for voucher-related pages.
 * Catches JavaScript errors in child components and displays a user-friendly fallback UI.
 */
class VoucherErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to Sentry if available
    if (typeof Sentry !== 'undefined' && Sentry.captureException) {
      Sentry.setContext('voucherErrorBoundary', {
        componentStack: errorInfo.componentStack,
      });
      Sentry.captureException(error);
    }
    console.error('VoucherErrorBoundary caught error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorWrapper>
          <ErrorIcon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </ErrorIcon>
          <ErrorTitle>Something went wrong</ErrorTitle>
          <ErrorMessage>
            We encountered an unexpected error. Your payment may have been
            processed successfully. Please check your email for confirmation or
            contact support if you need assistance.
          </ErrorMessage>
          <div>
            <RetryButton onClick={this.handleRetry}>Try Again</RetryButton>
            <HomeButton onClick={this.handleGoHome}>Go to Dashboard</HomeButton>
          </div>
        </ErrorWrapper>
      );
    }

    return this.props.children;
  }
}

VoucherErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default VoucherErrorBoundary;
